import React, { useEffect, useRef, useState } from 'react';
import { Box, Popper, Typography } from '@mui/material';

// Animation and timing constants (in milliseconds)
const POPOVER_SLIDE_DURATION = 500;
const POPOVER_DELAY = 2000;
const AUTO_DISMISS_DELAY = 5000; // 5 seconds

interface SidebarNotificationProps {
  children: React.ReactNode;
  enabled?: boolean;
  messages?: string[];
  showPopoverDelay?: number;
  hidePopoverAfter?: number;
  onShow?: () => void;
  /** CSS selector for the element to center the notification on */
  targetSelector?: string;
}

export const SidebarNotification: React.FC<SidebarNotificationProps> = ({
  children,
  enabled = false,
  messages = [],
  showPopoverDelay = POPOVER_DELAY,
  hidePopoverAfter,
  onShow,
  targetSelector,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [highlightEnabled, setHighlightEnabled] = useState(false);
  const [hasCalledOnShow, setHasCalledOnShow] = useState(false);
  const [hasStartedShowing, setHasStartedShowing] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  // Find target element if selector is provided
  useEffect(() => {
    if (targetSelector) {
      const element = document.querySelector(targetSelector) as HTMLElement;
      setTargetElement(element);
    } else {
      setTargetElement(null);
    }
  }, [targetSelector]);

  // Reset states when disabled
  useEffect(() => {
    if (!enabled) {
      setHasCalledOnShow(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled && messages.length > 0) {
      // Reset exit state when re-enabling
      setIsExiting(false);
      // Enable highlight immediately
      setHighlightEnabled(true);
      setHasStartedShowing(true);

      // Show popover after delay
      const showTimer = setTimeout(() => {
        setPopoverOpen(true);
      }, showPopoverDelay);

      // Optionally hide popover after specified duration
      let hideTimer: NodeJS.Timeout;
      if (hidePopoverAfter) {
        hideTimer = setTimeout(() => {
          setIsExiting(true);
          // Give time for exit animation before closing
          setTimeout(() => {
            setPopoverOpen(false);
            setHasStartedShowing(false);
          }, POPOVER_SLIDE_DURATION);
        }, showPopoverDelay + hidePopoverAfter);
      }

      return () => {
        clearTimeout(showTimer);
        if (hideTimer) clearTimeout(hideTimer);
      };
    } else if (!hasStartedShowing) {
      // Only hide if we haven't started showing yet
      setPopoverOpen(false);
      setIsExiting(false);
      setHighlightEnabled(false);
    }
  }, [enabled, messages, showPopoverDelay, hidePopoverAfter, hasStartedShowing]);

  // Call onShow when popover actually opens (only once)
  useEffect(() => {
    if (popoverOpen && onShow && !hasCalledOnShow) {
      onShow();
      setHasCalledOnShow(true);
    }
  }, [popoverOpen, onShow, hasCalledOnShow]);

  // Disable highlight 3 seconds after popover closes
  useEffect(() => {
    if (!popoverOpen && highlightEnabled) {
      const highlightTimer = setTimeout(() => {
        setHighlightEnabled(false);
      }, 3000);
      return () => clearTimeout(highlightTimer);
    }
  }, [popoverOpen, highlightEnabled]);

  // Auto-dismiss after 5 seconds
  useEffect(() => {
    if (popoverOpen && !hidePopoverAfter) {
      const dismissTimer = setTimeout(() => {
        setIsExiting(true);
        // Give time for exit animation
        setTimeout(() => {
          setPopoverOpen(false);
          setHasStartedShowing(false);
        }, POPOVER_SLIDE_DURATION);
      }, AUTO_DISMISS_DELAY);

      return () => clearTimeout(dismissTimer);
    }
  }, [popoverOpen, hidePopoverAfter]);

  return (
    <>
      <Box
        ref={anchorRef}
        sx={{
          position: 'relative',
          width: '100%',
          transition: highlightEnabled
            ? 'box-shadow 0.3s ease, border-color 0.3s ease'
            : 'none',
          boxShadow: highlightEnabled
            ? '0 0 0 3px rgba(217, 30, 108, 0.3), 0 0 20px rgba(217, 30, 108, 0.2)'
            : 'none',
          borderRadius: 1,
        }}
      >
        {children}
      </Box>
      <Popper
        open={popoverOpen && messages.length > 0}
        anchorEl={targetElement || anchorRef.current}
        placement={targetElement ? 'top' : 'right'}
        style={{ zIndex: 9999 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: targetElement ? [0, 16] : [16, 0], // Center on target or move right from box
            },
          },
        ]}
      >
        <Box
          sx={{
            position: 'relative',
            minWidth: 'max-content',
            animation: isExiting
              ? `slideOutLeft ${POPOVER_SLIDE_DURATION}ms cubic-bezier(0.68, -0.55, 0.265, 1.55)`
              : popoverOpen
                ? `slideInLeft ${POPOVER_SLIDE_DURATION}ms cubic-bezier(0.68, -0.55, 0.265, 1.55)`
                : 'none',
            '@keyframes slideInLeft': {
              '0%': {
                transform: 'translateX(-40px)',
                opacity: 0,
              },
              '40%': {
                transform: 'translateX(10px)',
                opacity: 1,
              },
              '60%': {
                transform: 'translateX(-5px)',
              },
              '80%': {
                transform: 'translateX(3px)',
              },
              '100%': {
                transform: 'translateX(0)',
              },
            },
            '@keyframes slideOutLeft': {
              '0%': {
                transform: 'translateX(0)',
                opacity: 1,
              },
              '100%': {
                transform: 'translateX(-100px)',
                opacity: 0,
              },
            },
            '&::before': targetElement
              ? {
                  // Arrow pointing down when centered on target
                  content: '""',
                  position: 'absolute',
                  left: '50%',
                  bottom: '-8px',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid #2c2c2c',
                  zIndex: 1,
                }
              : {
                  // Arrow pointing left when anchored to sidebar
                  content: '""',
                  position: 'absolute',
                  left: '-8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '8px solid transparent',
                  borderBottom: '8px solid transparent',
                  borderRight: '8px solid #2c2c2c',
                  zIndex: 1,
                },
          }}
        >
          {/* Black bubble with text */}
          <Box
            sx={{
              backgroundColor: '#2c2c2c',
              color: 'white',
              borderRadius: '12px',
              padding: '20px 24px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                color: 'white',
              }}
            >
              {messages[0] || ''}
            </Typography>
          </Box>
        </Box>
      </Popper>
    </>
  );
};

